import {useEffect, useRef} from 'react';
import {Form, Select} from 'antd';
import {PaginatedSelect, usePaginatedOptions} from 'components/paginatedSelect';
import {GET_ASSET_CLASS_INSTRUMENTS} from 'graphql/queries/assetManagement';

/**
 * Dividends are addressed by financial instrument, not by company.
 *
 * The investing service resolves a payout's whole taxonomy — issuer company,
 * instrument key, asset class — from `instrumentId` alone, and rejects the old
 * `companyId` / `investmentCategory` selectors outright. So these screens ask
 * for a type only to narrow the instrument list; the type itself is never sent.
 *
 * IPO is deliberately absent: an IPO instrument cannot receive a payout before
 * it lists, and the service rejects one.
 */
export const dividendInstrumentTypes = [
  {label: 'Stock', instrumentKey: 'PUBLIC_EQUITY', assetClassKey: 'EQUITIES'},
  {label: 'Bond', instrumentKey: 'BOND', assetClassKey: 'FIXED_INCOMES'},
  {label: 'Mutual fund', instrumentKey: 'MUTUAL_FUND', assetClassKey: 'FUNDS'},
  {
    label: 'Venture',
    instrumentKey: 'PRIVATE_EQUITY',
    assetClassKey: 'EQUITIES',
  },
  {
    label: 'Venture fund',
    instrumentKey: 'VENTURE_FUND',
    assetClassKey: 'FUNDS',
  },
];

export const dividendInstrumentType = instrumentKey =>
  dividendInstrumentTypes.find(type => type.instrumentKey === instrumentKey);

/** The instrument-type select. Its value is an `InstrumentKey`. */
export const InstrumentTypeField = ({name = 'instrumentType', required}) => (
  <Form.Item
    name={name}
    label="Instrument Type"
    tooltip="Narrows the instrument list below. The payout itself is addressed by instrument."
    rules={[{required, message: 'This is a Required Field'}]}>
    <Select allowClear placeholder="Select an instrument type">
      {dividendInstrumentTypes.map(type => (
        <Select.Option key={type.instrumentKey} value={type.instrumentKey}>
          {type.label}
        </Select.Option>
      ))}
    </Select>
  </Form.Item>
);

/**
 * Instruments of the chosen type, paged and searched on the server.
 *
 * The value is the financial instrument's `id` — never a company id, and never
 * an `id;issuerId` pair. The issuer is the service's to resolve.
 */
export const InstrumentSelect = ({value, onChange, instrumentKey}) => {
  const type = dividendInstrumentType(instrumentKey);
  const pagination = usePaginatedOptions({
    query: GET_ASSET_CLASS_INSTRUMENTS,
    skip: !type,
    buildVariables: (page, search, limit) => ({
      input: {
        explore: false,
        instrumentKey: type?.instrumentKey,
        assetClassKey: type?.assetClassKey,
        paginate: true,
        page,
        limit,
        ...(search && {search}),
      },
    }),
    readPage: data => ({
      rows: (data?.getFinancialInstruments?.data ?? []).filter(row => row?.id),
      hasNextPage: data?.getFinancialInstruments?.pagination?.hasNextPage,
    }),
    toOption: row => ({
      value: row.id,
      label: row.ticker ? `${row.ticker} · ${row.name}` : row.name,
    }),
  });

  return (
    <PaginatedSelect
      value={value}
      onChange={onChange}
      pagination={pagination}
      disabled={!type}
      placeholder={
        type ? 'Search instruments' : 'Pick an instrument type first'
      }
      notFound="No matching instruments"
    />
  );
};

/** Type + instrument, as the two form items every dividend form needs. */
export const InstrumentFields = ({form, required}) => {
  const instrumentKey = Form.useWatch('instrumentType', form);
  const previousKey = useRef(instrumentKey);

  // An instrument belongs to exactly one type, so a type change invalidates
  // whatever was picked. Leaving it would submit a bond id under "Stock".
  useEffect(() => {
    if (previousKey.current !== instrumentKey) {
      previousKey.current = instrumentKey;
      form.setFieldsValue({instrumentId: undefined});
    }
  }, [instrumentKey, form]);

  return (
    <>
      <InstrumentTypeField required={required} />
      <Form.Item
        name="instrumentId"
        label="Instrument"
        rules={[{required, message: 'This is a Required Field'}]}>
        <InstrumentSelect instrumentKey={instrumentKey} />
      </Form.Item>
    </>
  );
};
